import type { Klass, LexicalEditor, LexicalNode, NodeKey } from 'lexical';
import { $getNodeByKey, COLLABORATION_TAG } from 'lexical';

import { CONTENT_LOAD_TAG } from '../core/updateTags';

type Callbacks<T> = {
  onAdded: (value: T) => void;
  onRemoved: (value: T) => void;
};

/** Lexical's tag for the first call, which reports the nodes already there when the listener is registered. */
const REGISTRATION_TAG = 'registerMutationListener';

/** Updates whose nodes were already there, or someone else's to report. */
const UNREPORTED_TAGS = [CONTENT_LOAD_TAG, COLLABORATION_TAG, REGISTRATION_TAG];

/**
 * Reports when a value gets its first node of `klass` and loses its last one (e.g. a mentioned user, a placeholder key).
 * - `read` gives a node's value (`null` skips it); `idOf` says which values are the same one.
 * - Counts per id, so two nodes for one value report once, and so does removing one of the two.
 * - Unreported updates (loads, collaborators' edits) still update the counts, so a later local edit reports correctly.
 */
export const registerNodeTracking = <T>(
  editor: LexicalEditor,
  klass: Klass<LexicalNode>,
  read: (node: LexicalNode | null) => T | null,
  idOf: (value: T) => string,
  { onAdded, onRemoved }: Callbacks<T>,
): (() => void) => {
  // A destroyed node can't be read any more, so each one's value is remembered from when it appeared
  const valueByKey = new Map<NodeKey, T>();
  const counts = new Map<string, number>();

  return editor.registerMutationListener(klass, (mutations, { updateTags }) => {
    const touched = new Map<string, { value: T; before: number }>();
    const change = (value: T, by: number) => {
      const id = idOf(value);
      const count = counts.get(id) ?? 0;
      if (!touched.has(id)) touched.set(id, { value, before: count });
      counts.set(id, count + by);
    };

    editor.getEditorState().read(() => {
      for (const [key, mutation] of mutations) {
        const previous = valueByKey.get(key);
        if (previous !== undefined) {
          valueByKey.delete(key);
          change(previous, -1);
        }
        if (mutation === 'destroyed') continue;
        const value = read($getNodeByKey(key));
        if (value === null) continue;
        valueByKey.set(key, value);
        change(value, +1);
      }
    });

    if (UNREPORTED_TAGS.some((tag) => updateTags.has(tag))) return;
    // Compared once per update, so an `updated` node (counted out, then in) reports nothing
    for (const [id, { value, before }] of touched) {
      const after = counts.get(id) ?? 0;
      if (before === 0 && after > 0) onAdded(value);
      if (before > 0 && after === 0) onRemoved(value);
    }
  });
};
